
// src/features/trainings/TrainingsService.js
import { customFetch } from '../../customFetch';
import { TRAININGS } from '../../utils/URLS';
import dayjs from 'dayjs';

/**
 * Servicio para gestionar las operaciones de API relacionadas con capacitaciones
 */
const TrainingsService = {
  /**
   * Obtener todas las capacitaciones
   * @returns {Promise<Array>} Lista de capacitaciones
   */
  async getAllTrainings() {
    try {
      const response = await customFetch(TRAININGS.LIST, {});
      return await response.json();
    } catch (error) {
      console.error('Error en getAllTrainings:', error);
      throw error;
    }
  },
/**
 * Obtener la lista de instructores de capacitaciones disponibles
 * @returns {Promise<string[]>} - Lista de nombres de instructores
 */
async getInstructors() {
  try {
    const response = await customFetch(TRAININGS.INSTRUCTORS, {});
    return await response.json();
  } catch (error) {
    console.error('Error en getInstructors:', error);
    return [];
  }
},
  /**
   * Filtrar capacitaciones según criterios
   * @param {string} params - Parámetros de filtrado
   * @returns {Promise<Array>} Lista de capacitaciones filtradas
   */
  async filterTrainings(params) {
    try {
      const url = params && params.length > 0 
        ? `${TRAININGS.SEARCH}?${params}` 
        : TRAININGS.LIST;
      
      const response = await customFetch(url, {});
      return await response.json();
    } catch (error) {
      console.error('Error en filterTrainings:', error);
      throw error;
    }
  },

  /**
   * Obtener una capacitación específica
   * @param {string} id - ID de la capacitación
   * @returns {Promise<Object>} Datos de la capacitación
   */
  async getTraining(id) {
    try {
      const response = await customFetch(`${TRAININGS.LIST}${id}`, {});
      return await response.json();
    } catch (error) {
      console.error(`Error en getTraining (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Crear una nueva capacitación
   * @param {Object} trainingData - Datos de la capacitación
   * @returns {Promise<Object>} Capacitación creada
   */
  async createTraining(trainingData) {
    try {
      // Aseguramos que las fechas están en formato ISO
      const dataToSend = {
        ...trainingData,
        date: trainingData.date ? dayjs(trainingData.date).format('YYYY-MM-DD') : null,
        location: trainingData.location || 'generica' // Valor por defecto
      };

      const response = await customFetch(TRAININGS.LIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      
      return await response.json();
    } catch (error) {
      console.error('Error en createTraining:', error);
      throw error;
    }
  },

  /**
   * Actualizar una capacitación existente
   * @param {Object} trainingData - Nuevos datos de la capacitación
   * @param {string} id - ID de la capacitación
   * @returns {Promise<Object>} Capacitación actualizada
   */
  async updateTraining(trainingData, id) {
    try {
      // Crear un objeto con lo necesario para actualizar
      // Convertir los IDs de personas a la estructura esperada
      const trainingToUpdate = {
        ...trainingData,
        date: trainingData.date ? dayjs(trainingData.date).format('YYYY-MM-DD') : null,
        personsIds: trainingData.persons?.map(person => person.id) || []
      };
      
      // Eliminar la propiedad persons para evitar datos duplicados
      delete trainingToUpdate.persons;

      const response = await customFetch(`${TRAININGS.LIST}${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trainingToUpdate)
      });
      
      return {
        statusCode: response.status,
        data: await response.json()
      };
    } catch (error) {
      console.error(`Error en updateTraining (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Eliminar una capacitación
   * @param {string} id - ID de la capacitación a eliminar
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deleteTraining(id) {
    try {
      const response = await customFetch(`${TRAININGS.LIST}${id}`, {
        method: 'DELETE'
      });
      
      return await response.json();
    } catch (error) {
      console.error(`Error en deleteTraining (ID: ${id}):`, error);
      throw error;
    }
  },

  /**
   * Asignar una persona a una capacitación
   * @param {Object} training - Datos de la capacitación incluyendo personas
   * @param {string} trainingId - ID de la capacitación
   * @returns {Promise<Object>} Resultado de la operación
   */
  async assignPersonToTraining(training, trainingId) {
    try {
      // Preparamos el objeto sin las personas y con fecha en formato ISO
      const trainingForUpdate = {
        ...training,
        date: training.date ? dayjs(training.date).format('YYYY-MM-DD') : null,
        personsIds: training.personsIds || []
      };
      
      delete trainingForUpdate.persons;

      const response = await customFetch(`${TRAININGS.LIST}${trainingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trainingForUpdate)
      });
      
      const data = await response.json();
      
      return {
        statusCode: response.status,
        data: data
      };
    } catch (error) {
      console.error(`Error en assignPersonToTraining (ID: ${trainingId}):`, error);
      throw error;
    }
  }
};

export default TrainingsService;